import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  getFormNames,
  getFormSyncErrors,
  getFormValues,
  isValid,
  submit as reduxSubmit,
  setSubmitFailed,
  touch,
} from "redux-form";

import {
  generateRandomNumber,
  setCbtToken,
  setCustomerDevice,
  setCustomerName,
  setCustomerToken,
} from "shared/data/customerTokens";
import { AppState } from "shared/data/reducers";
import detailCustomer2 from "shared/gol-api/detailCustomer2";
import { getCbtTokenFromClientDetail } from "shared/hooks/login";
import { genderFromNamePrefix, scrollTo } from "shared/lib/functions";
import { createCustomer, getCustomerToken } from "shared/lib/golCopy";
import { setInitialRequestorConfig } from "shared/lib/requestorFunctions";
import { formattedMessageParams } from "shared/messages";
import Logger from "shared/services/Logger";

import pageWithIntl from "@components/PageWithIntl";
import Form from "@components/Register/Form";
import FormattedMessage from "@components/UI/FormattedMessage";
import StandardLayout from "@components/UI/Layouts/StandardLayout";
import Spinner from "@components/UI/Spinner";
import useModalAlert from "@components/UI/useModalAlert";
import PageSeo from "@components/Utils/PageSeo";

import { pageDataLayer } from "@lib/dataLayers";
import resolveRedirectAfterLogin from "@lib/redirectAfterLogin";
import { redirect } from "@lib/webFunctions";

const { title, approvalRequiredMessage } = defineMessages({
  title: formattedMessageParams("Screen.Register.title"),
  approvalRequiredMessage: formattedMessageParams(
    "Screen.Register.approvalRequiredMessage"
  ),
});

const Index = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const intl = useIntl();
  const registerFormRef = useRef();

  const countriesArr = [];
  const { countries } = useSelector(({ storage }: AppState) => {
    return storage;
  });

  const country = useSelector(({ storage }: AppState) => {
    return storage.frontendSettings?.dealerFrontendSettings?.defaultCountry;
  });
  Object.entries(countries).forEach(([value, label]) => {
    countriesArr.push({ value, label });
  });
  const preselectedCountry = countriesArr.find((i) => i.value === country);

  const [billingForm, setBillingForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean | string>(false);

  const [ModalAlertError, showError] = useModalAlert();

  const [ApprovalRequiredWarning, showApprovalRequiredWarning] = useModalAlert({
    hideHeader: true,
    onClose: () => redirect(router),
  });

  const [ModalAlertSuccess, showSuccess] = useModalAlert({
    hideHeader: true,
    onClose: () => redirect(router),
  });

  useEffect(() => {
    scrollTo("#register-main");
  }, []);

  const forms = useSelector((store: AppState) =>
    getFormNames()(store).map((formName) => ({
      formName,
      isValid: isValid(formName)(store),
      values: getFormValues(formName)(store),
      errors: getFormSyncErrors(formName)(store),
    }))
  );

  const phonePrefix = useSelector(
    (store: AppState) =>
      store.storage.frontendSettings?.dealerFrontendSettings?.phonePrefix
  );

  const { user } = useSelector((state: AppState) => {
    return { user: state.user };
  });

  useEffect(() => {
    pageDataLayer("other", user);
  }, [user]);

  const submit = async () => {
    const failHandler = () => {
      dispatch(
        touch(resultForm[0].formName, ...Object.keys(resultForm[0].errors))
      );
      setSubmitFailed("registerForm", ...Object.keys(resultForm[0].errors));

      // @ts-ignore
      dispatch(reduxSubmit("registerForm"));
    };

    const resultForm = forms.filter((form) => form.formName === "registerForm");
    const { company, street, city, postalCode, ic, dic } = resultForm[0].values;
    const { errors } = resultForm[0];

    if (company || street || city || postalCode || ic || dic) {
      if (
        !errors.company ||
        !errors.street ||
        !errors.city ||
        !errors.postalCode ||
        !ic ||
        !dic
      ) {
        setBillingForm(true);
        failHandler();
      }
    }

    if (resultForm[0].isValid) {
      return register(resultForm[0].values);
    }
    return failHandler();
  };

  const register = async (values) => {
    if (values.password !== values.passwordCheck) {
      showError("Zadaná hesla se neshodují.");
      return;
    }

    setIsLoading(true);

    const namePrefix = values.title ? values.title.value : "mr";
    const { company, ic, dic, street, city, postalCode } = values;
    const countryObject = values.country;
    const newCustomer = {
      Username: values.username,
      Password: values.password,
      Gender: genderFromNamePrefix(namePrefix),
      NamePrefix: namePrefix,
      PhoneNumber: values.telephone,
      Email: values.email,
      GivenName: values.firstName,
      Surname: values.surname,
      Newsletter: values.newsletter,
      SpecialOffers: values.specialOffers,
      DeviceId: generateRandomNumber(10000000, 99999999),
      DeviceName:
        (window.navigator && window.navigator.userAgent) ||
        generateRandomNumber(1000, 999999999),
      Namespace: null,
      company,
      ic: ic || "",
      dic: dic || "",
      street,
      city,
      postalCode,
      country: countryObject.value,
    };

    const createdCustomer = await createCustomer(newCustomer);

    if (createdCustomer.CreateCustomerError_1) {
      setIsLoading(false);
      setError(createdCustomer.CreateCustomerError_1.Error[0].$t);
      return false;
    }

    const approvalRequiredWarningCode = "5031";
    const approvalRequiredWarning = createdCustomer?.CreateCustomerResponse_1?.Warnings?.Warning?.find(
      ({ Code }) => Code === approvalRequiredWarningCode
    );
    if (approvalRequiredWarning) {
      setIsLoading(false);
      showApprovalRequiredWarning(intl.formatMessage(approvalRequiredMessage));
      return false;
    }

    const customerTokenReq = await getCustomerToken(newCustomer);
    const customerToken = customerTokenReq && customerTokenReq.token;

    if (customerTokenReq && customerTokenReq?.needsConfirmation) {
      showSuccess(
        intl.formatMessage({
          id: "EmailConfirmation.description",
        })
      );
    }

    await login(customerToken, newCustomer);

    setIsLoading(false);

    return redirectAfterLogin(
      customerToken,
      customerTokenReq?.needsConfirmation
    );
  };

  const redirectAfterLogin = (customerToken, isConfirmEmail) => {
    if (customerToken) {
      resolveRedirectAfterLogin(router);
    } else if (!isConfirmEmail) {
      setError(
        intl.formatMessage({
          id: "Register.errorOccurredWhileLoggingIn",
        })
      );
    }
  };

  const handleSelectValidation = (errorFields) => {
    for (let i = 0; i < errorFields.length; i++) {
      const fieldName = `registerForm-${errorFields[i]}`;
      let selectParentDiv = "";
      if (fieldName.includes("title")) {
        selectParentDiv = `registerForm-title-wrapper`;
        if (
          selectParentDiv !== "" &&
          document.querySelectorAll(`#${selectParentDiv}`).length
        ) {
          document
            .querySelectorAll(`#${selectParentDiv}`)[0]
            .classList.add("input-wrapper-error");
        }
      }
    }
  };

  const submitFail = (errors) => {
    const errorFields = Object.keys(errors);
    handleSelectValidation(errorFields);
    for (let i = 0; i < errorFields.length; i++) {
      const fieldName = `registerForm-${errorFields[i]}`;
      if (document.querySelectorAll(`#${fieldName}`).length) {
        if (fieldName.includes("birthDate")) {
          const selectParentDiv = `registerForm-birthDate`;
          scrollTo(`#${selectParentDiv}`);
          break;
        } else if (fieldName.includes("expirationDate")) {
          const selectParentDiv = `registerForm-expirationDate`;
          scrollTo(`#${selectParentDiv}`);
          break;
        } else {
          scrollTo(`#${fieldName}`);
          break;
        }
      }
    }
  };

  const login = async (customerToken, newCustomer) => {
    await setCustomerToken(customerToken);
    await setCustomerName(`${newCustomer.GivenName}---${newCustomer.Surname}`);
    const device = { id: newCustomer?.DeviceId, name: newCustomer?.DeviceName };
    await setCustomerDevice(device);

    const clientDetail = await detailCustomer2();

    if (clientDetail?.data?.formattedData) {
      await setCbtToken(
        getCbtTokenFromClientDetail(clientDetail.data.formattedData)
      );
    }
  };

  return (
    <div className="container" id="register-main">
      <PageSeo
        title={{
          defaultTitle: intl.formatMessage(title),
        }}
      />
      <div className="clearfix" />

      <div className="universal-content-box">
        <div className="result-box-content">
          <h1 className="universal-h1">
            <FormattedMessage id="Register.newRegistration" />
          </h1>
          <h3 className="universal-h3">
            <FormattedMessage id="Register.loginInformation" />
          </h3>
          <p className="universal-h-description">
            <FormattedMessage id="Register.description" />
          </p>
          <div className="clearfix" />
          {isLoading ? <Spinner /> : null}
          <Form
            intl={intl}
            initialValues={{
              email: "@",
              newsletter: false,
              specialOffers: false,
              telephone: phonePrefix,
              country: preselectedCountry,
            }}
            billingForm={billingForm}
            setBillingForm={setBillingForm}
            ref={registerFormRef}
            onSubmit={submit}
            onSubmitFail={submitFail}
          />
          <div style={{ position: "relative", height: "90px" }}>
            {error ? (
              <span className="register-error-message">{error}</span>
            ) : null}
            <button
              type="button"
              className="orange-button button main-search-button button-secondary-color button-confirm button-error-box"
              onClick={submit}
            >
              <FormattedMessage id="Screen.Register.title" />
            </button>
          </div>
        </div>
      </div>
      <ModalAlertError />
      <ModalAlertSuccess />
      <ApprovalRequiredWarning />
    </div>
  );
};

function RegisterLayout(page) {
  return (
    <StandardLayout showCondensedHeaderLogoStripe hideSearchForm>
      {page}
    </StandardLayout>
  );
}

Index.getLayout = RegisterLayout;

Index.getInitialProps = async (initialProps) => {
  try {
    const { domain } = await setInitialRequestorConfig(initialProps);
    return { domain };
  } catch (e) {
    Logger.error("Error Initial. ", e);
    return {};
  }
};

export default pageWithIntl(Index);
